/**
 * Typed API client.
 *
 * Every economic mutation carries an Idempotency-Key so a retry after a dropped
 * connection replays the original result instead of charging twice.
 */

import type {
  AdminAreasResponse,
  ChatMessage,
  ChunkStatusResponse,
  DashboardResponse,
  ErrorCode,
  FacilitySummary,
  FloorPlanResponse,
  LeaderboardResponse,
  MapLabelsResponse,
  MarketBookResponse,
  OpenOrder,
  PlaceOrderResponse,
  PropertyDetail,
  PropertyOfferSummary,
  WorldTileResponse,
} from "@parcelbound/contracts";

const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "NETWORK",
    message: string,
    readonly currentVersion?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

async function request<T>(
  path: string,
  init: RequestInit & { idempotent?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("content-type", "application/json");
  if (init.idempotent) headers.set("idempotency-key", newIdempotencyKey());

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: "same-origin" });
  } catch (err) {
    throw new ApiError(0, "NETWORK", err instanceof Error ? err.message : "network error");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const e = body as { code?: ErrorCode; message?: string; currentVersion?: number };
    throw new ApiError(
      res.status,
      e?.code ?? "INTERNAL",
      e?.message ?? `request failed (${res.status})`,
      e?.currentVersion,
    );
  }
  return body as T;
}

export interface PortfolioResponse {
  parcels: Array<{
    id: string;
    chunkId: string;
    centroid: [number, number];
    areaM2: number;
    landUse: string;
    appraisedValue: string;
    buildingCount: number;
    version: number;
  }>;
  buildings: Array<{
    id: string;
    parcelId: string;
    chunkId: string;
    centroid: [number, number];
    label: string;
    archetype: string;
    floors: number;
    condition: number;
    appraisedValue: string;
    unitCount: number;
    leasedUnitCount: number;
    version: number;
  }>;
}

export interface InventoryResponse {
  items: Array<{
    commodity: string;
    name: string;
    family: string;
    qty: number;
    basePrice: string;
  }>;
}

export interface TickerEntry {
  commodity: string;
  name: string;
  price: string;
  basePrice: string;
  changePct: number;
  volume24h: number;
  traded: boolean;
}

export interface TickerResponse {
  entries: TickerEntry[];
  updatedAt: string;
}

export interface CommodityListResponse {
  commodities: Array<{
    id: string;
    name: string;
    family: string;
    basePrice: string;
    bulk: number;
    npcLiquidity: boolean;
  }>;
}

export interface LeaseListResponse {
  leases: Array<{
    id: string;
    unitId: string;
    unitLabel: string;
    buildingLabel: string;
    role: "landlord" | "tenant";
    counterpartyName: string;
    rentPerDay: string;
    deposit: string;
    endsAt: string;
    centroid: [number, number];
  }>;
}

export interface SettleResponse {
  homeChunkId: string;
  grant: string;
  grantTradableAfter: string;
  foodCartPermit: boolean;
  suggestedStarterUnit: {
    unitId: string;
    buildingId: string;
    label: string;
    use: string;
    areaM2: number;
    distanceM: number;
  } | null;
}

export interface NpcTenantResponse {
  accepted: boolean;
  leaseId?: string;
  tenantName?: string;
  demand: number;
  fairRentPerDay: string;
  askingRentPerDay: string;
  hint?: string;
}

export interface BusinessCatalogResponse {
  facilityTypes: Array<{
    id: string;
    name: string;
    unitUses: string[];
    requiresLand: boolean;
    mobile: boolean;
    installCost: string;
    workerSlots: number;
    skill: string;
    storageUnits: number;
    upkeepPerDay: string;
    isShop: boolean;
  }>;
  workOrders: Array<{
    id: string;
    name: string;
    facility: string;
    inputs: Array<{ commodity: string; qty: number }>;
    outputs: Array<{ commodity: string; qty: number }>;
    minutes: number;
    kind: "recipe" | "grow" | "extract";
  }>;
  vehicleClasses: Array<{
    id: string;
    name: string;
    cargo: number;
    speedMPerMin: number;
    price: string;
    upkeepPerDay: string;
  }>;
  livestock: Array<{ id: string; name: string; price: string; penM2PerHead: number }>;
}

export interface LogisticsResponse {
  vehicles: Array<{ id: string; class: string; name: string; laneId: string | null }>;
  lanes: Array<{
    id: string;
    from: { facilityId: string | null; label: string; at: [number, number] | null };
    to: { facilityId: string | null; label: string; at: [number, number] | null };
    commodity: string;
    movedTotal: number;
    distanceM: number | null;
    estUnitsPerDay: number;
    vehicles: Array<{ id: string; class: string; name: string }>;
    drivers: Array<{ id: string; name: string; skillLevel: number; wagePerDay: string }>;
  }>;
}

export const api = {
  register: (email: string, password: string, displayName: string) =>
    request<{ id: string; displayName: string; emailVerified: boolean }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName }),
    }),

  login: (email: string, password: string) =>
    request<{ id: string; displayName: string; emailVerified: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  session: () =>
    request<{ id: string; displayName: string; homeChunkId: string | null; isStaff?: boolean }>(
      "/auth/session",
    ),

  dashboard: () => request<DashboardResponse>("/me/dashboard"),

  settle: (lon: number, lat: number) =>
    request<SettleResponse>("/me/settle", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ lon, lat }),
    }),

  chunksForViewport: (b: { west: number; south: number; east: number; north: number }) =>
    request<{ chunkIds: string[] }>(
      `/world/chunks/for-viewport?west=${b.west}&south=${b.south}&east=${b.east}&north=${b.north}`,
    ),

  chunkStatus: (chunkIds: string[]) =>
    request<ChunkStatusResponse>(
      `/world/chunks/status?chunkIds=${encodeURIComponent(chunkIds.join(","))}`,
    ),

  ensureChunks: (chunkIds: string[]) =>
    request<ChunkStatusResponse>("/world/chunks/ensure", {
      method: "POST",
      body: JSON.stringify({ chunkIds }),
    }),

  // The chunk id's slashes are real path segments: proxies decode %2F (the
  // production ingress 307s it away), so the encoded spelling 404s when
  // deployed. The API serves the three-segment form canonically.
  chunkFeatures: (chunkId: string) =>
    request<WorldTileResponse>(`/world/chunks/${chunkId}/features`),

  property: (id: string) => request<PropertyDetail>(`/properties/${id}`),

  /** The plan of one floor: its units and the rooms inside them. */
  floorPlan: (buildingId: string, floor: number) =>
    request<FloorPlanResponse>(`/buildings/${buildingId}/floorplan?floor=${floor}`),

  purchase: (id: string, kind: "land" | "building", expectedPrice: string, expectedVersion: number) =>
    request<{ propertyId: string; pricePaid: string; version: number }>(
      `/properties/${id}/purchase`,
      {
        method: "POST",
        idempotent: true,
        body: JSON.stringify({ kind, expectedPrice, expectedVersion }),
      },
    ),

  construct: (parcelId: string, archetype: string, floors: number, expectedVersion: number) =>
    request<{ buildingId: string; cost: string; unitsCreated: number }>("/properties/construct", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ parcelId, archetype, floors, expectedVersion }),
    }),

  portfolio: () => request<PortfolioResponse>("/me/portfolio"),

  createLeaseListing: (input: {
    unitId: string;
    rentPerDay: string;
    deposit: string;
    leaseDays: number;
    tenantPolicy: "npc" | "player" | "both";
    autoRenew: boolean;
  }) =>
    request<{ listingId: string }>("/lease-listings", {
      method: "POST",
      body: JSON.stringify({ ...input, permittedUses: [] }),
    }),

  seekNpcTenant: (listingId: string) =>
    request<NpcTenantResponse>(`/lease-listings/${listingId}/seek-npc-tenant`, {
      method: "POST",
      idempotent: true,
    }),

  leases: () => request<LeaseListResponse>("/me/leases"),

  commodities: () => request<CommodityListResponse>("/market/commodities"),

  ticker: () => request<TickerResponse>("/market/ticker"),

  orders: () => request<{ orders: OpenOrder[] }>("/market/orders"),

  cancelOrder: (id: string) =>
    request<{ ok: boolean }>(`/market/orders/${id}`, { method: "DELETE", idempotent: true }),

  ledger: () =>
    request<{
      entries: Array<{
        id: string;
        amount: string;
        balanceAfter: string;
        reason: string;
        memo: string | null;
        account: string;
        at: string;
      }>;
    }>("/me/ledger"),

  savingsTransfer: (direction: "to_savings" | "to_wallet", amount: string) =>
    request<{ ok: boolean }>("/me/savings/transfer", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ direction, amount }),
    }),

  book: (commodity: string) => request<MarketBookResponse>(`/market/${commodity}/book`),

  placeOrder: (commodity: string, side: "buy" | "sell", price: string | null, qty: number) =>
    request<PlaceOrderResponse>("/market/orders", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ commodity, side, price, qty }),
    }),

  inventory: () => request<InventoryResponse>("/me/inventory"),

  leaderboard: () => request<LeaderboardResponse>("/leaderboards/all_time"),

  /* Business */

  businessCatalog: () => request<BusinessCatalogResponse>("/business/catalog"),

  facilities: () => request<{ facilities: FacilitySummary[] }>("/me/facilities"),

  installFacility: (input: {
    facilityType: string;
    parcelId?: string;
    unitId?: string;
    lon?: number;
    lat?: number;
  }) =>
    request<{ facilityId: string }>("/facilities", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify(input),
    }),

  resumeFacility: (id: string) =>
    request<{ ok: boolean }>(`/facilities/${id}/resume`, { method: "POST", idempotent: true }),

  removeFacility: (id: string) =>
    request<{ ok: boolean }>(`/facilities/${id}`, { method: "DELETE" }),

  hireWorker: (facilityId: string) =>
    request<{ id: string; name: string; skillLevel: number; wagePerDay: string }>(
      `/facilities/${facilityId}/hire`,
      { method: "POST" },
    ),

  dismissWorker: (id: string) => request<{ ok: boolean }>(`/workers/${id}`, { method: "DELETE" }),

  setJob: (facilityId: string, workOrderId: string | null) =>
    request<{ ok: boolean }>(`/facilities/${facilityId}/job`, {
      method: "POST",
      body: JSON.stringify({ workOrderId }),
    }),

  setOffers: (facilityId: string, offers: Array<{ commodity: string; price: string }>) =>
    request<{ ok: boolean }>(`/facilities/${facilityId}/offers`, {
      method: "PUT",
      body: JSON.stringify({ offers }),
    }),

  buyRoom: (buildingId: string, floor: number, roomId: string, expectedPrice: string) =>
    request<{ pricePaid: string }>("/rooms/buy", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ buildingId, floor, roomId, expectedPrice }),
    }),

  worldOverview: () =>
    request<{ roads: Array<{ roadClass: string; line: Array<[number, number]> }> }>(
      "/world/overview",
    ),

  adminAreas: (b: { west: number; south: number; east: number; north: number }, dist: number) =>
    request<AdminAreasResponse>(
      `/world/admin-areas?west=${b.west}&south=${b.south}&east=${b.east}&north=${b.north}&dist=${Math.round(dist)}`,
    ),

  mapLabels: (b: { west: number; south: number; east: number; north: number }, dist: number) =>
    request<MapLabelsResponse>(
      `/world/labels?west=${b.west}&south=${b.south}&east=${b.east}&north=${b.north}&dist=${Math.round(dist)}`,
    ),

  wipeFacility: (facilityId: string) =>
    request<{ cleanliness: number }>(`/facilities/${facilityId}/wipe`, { method: "POST" }),

  prepFacility: (facilityId: string, workOrderId: string, batches: number) =>
    request<{ prepped: number; stock: Array<{ commodity: string; qty: number }> }>(
      `/facilities/${facilityId}/prep`,
      { method: "POST", body: JSON.stringify({ workOrderId, batches }) },
    ),

  myUnits: () =>
    request<{
      units: Array<{
        id: string;
        label: string;
        use: string;
        areaM2: number;
        buildingLabel: string;
        tenure: "owned" | "leased";
      }>;
    }>("/me/units"),

  buyLivestock: (facilityId: string, kind: string, heads: number) =>
    request<{ ok: boolean }>(`/facilities/${facilityId}/livestock`, {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ kind, heads }),
    }),

  /* Logistics */

  logistics: () => request<LogisticsResponse>("/me/logistics"),

  buyVehicle: (vehicleClass: string) =>
    request<{ vehicleId: string }>("/vehicles", {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ vehicleClass }),
    }),

  sellVehicle: (id: string) => request<{ ok: boolean }>(`/vehicles/${id}`, { method: "DELETE" }),

  assignVehicle: (id: string, laneId: string | null) =>
    request<{ ok: boolean }>(`/vehicles/${id}/assign`, {
      method: "POST",
      body: JSON.stringify({ laneId }),
    }),

  createLane: (fromFacilityId: string | null, toFacilityId: string | null, commodity: string) =>
    request<{ laneId: string }>("/logistics/lanes", {
      method: "POST",
      body: JSON.stringify({ fromFacilityId, toFacilityId, commodity }),
    }),

  deleteLane: (id: string) => request<{ ok: boolean }>(`/logistics/lanes/${id}`, { method: "DELETE" }),

  hireDriver: (laneId: string) =>
    request<{ id: string; name: string; skillLevel: number; wagePerDay: string }>(
      `/logistics/lanes/${laneId}/hire`,
      { method: "POST" },
    ),

  renovate: (buildingId: string) =>
    request<{ cost: string; condition: number }>(`/buildings/${buildingId}/renovate`, {
      method: "POST",
      idempotent: true,
    }),

  /* Social hardening */

  openDm: (playerId: string) =>
    request<{ id: string; kind: string; key: string; name: string }>("/social/dm", {
      method: "POST",
      body: JSON.stringify({ playerId }),
    }),

  blockPlayer: (playerId: string) =>
    request<{ ok: boolean }>("/social/blocks", {
      method: "POST",
      body: JSON.stringify({ playerId }),
    }),

  reportPlayer: (subjectPlayerId: string | null, context: string, body: string) =>
    request<{ reportId: string }>("/social/reports", {
      method: "POST",
      body: JSON.stringify({ subjectPlayerId, context, body }),
    }),

  /* Staff console */

  adminOverview: () =>
    request<{
      players: number;
      chunks: Record<string, number>;
      openReports: number;
      ledgerNetTotal: string;
      settledThroughMinute: number;
    }>("/admin/overview"),

  adminReports: () =>
    request<{
      reports: Array<{
        id: string;
        context: string;
        body: string;
        reporterName: string;
        subjectId: string | null;
        subjectName: string | null;
        createdAt: string;
      }>;
    }>("/admin/reports"),

  adminResolveReport: (id: string, outcome: "resolved" | "dismissed") =>
    request<{ ok: boolean }>(`/admin/reports/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    }),

  adminPlayers: (q: string) =>
    request<{
      players: Array<{
        id: string;
        displayName: string;
        email: string;
        wallet: string;
        createdAt: string;
        deleted: boolean;
        isStaff: boolean;
      }>;
    }>(`/admin/players?q=${encodeURIComponent(q)}`),

  adminAdjust: (playerId: string, amount: string, memo: string) =>
    request<{ ok: boolean }>(`/admin/players/${playerId}/adjust`, {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ amount, memo }),
    }),

  /* Direct property offers */

  makeOffer: (propertyId: string, kind: "land" | "building", amount: string) =>
    request<{ offerId: string }>(`/properties/${propertyId}/offers`, {
      method: "POST",
      idempotent: true,
      body: JSON.stringify({ kind, amount }),
    }),

  propertyOffers: () => request<{ offers: PropertyOfferSummary[] }>("/me/property-offers"),

  acceptOffer: (id: string) =>
    request<{ propertyId: string; amount: string }>(`/property-offers/${id}/accept`, {
      method: "POST",
      idempotent: true,
    }),

  declineOffer: (id: string) =>
    request<{ ok: boolean }>(`/property-offers/${id}/decline`, { method: "POST", idempotent: true }),

  withdrawOffer: (id: string) =>
    request<{ ok: boolean }>(`/property-offers/${id}/withdraw`, { method: "POST", idempotent: true }),

  /* Community */

  channels: () =>
    request<{ channels: Array<{ id: string; kind: string; key: string; name: string }> }>(
      "/social/channels",
    ),

  messages: (channelId: string) =>
    request<{ messages: ChatMessage[] }>(`/social/channels/${channelId}/messages`),

  postMessage: (channelId: string, body: string) =>
    request<{ id: string; sentAt: string }>(`/social/channels/${channelId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
};
